import { useState } from "react"
import { useTranslation } from "react-i18next"
import axios from "axios"
import MarketingLayout from "@/components/marketing/MarketingLayout"
import { buildContactPageSchema } from "@/components/structured-data"
import { useBrand } from "@/hooks/use-brand"
import { LOCALE_CONFIG } from "@/config/locale-domains"
import { API_BASE } from "@/config/api"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { toast } from "sonner"

export default function Contact() {
  const { t, i18n } = useTranslation()
  const brand = useBrand()
  const domain = LOCALE_CONFIG[brand.locale]?.domain || LOCALE_CONFIG.ro.domain
  const email = t("pages.contact.email")

  const jsonLd = buildContactPageSchema({
    name: brand.brand,
    url: `https://${domain}/contact`,
    email,
  })

  const [submitting, setSubmitting] = useState(false)

  async function onSubmit(e) {
    e.preventDefault()
    setSubmitting(true)
    const form = e.target
    try {
      await axios.post(`${API_BASE}/public/contact`, {
        name: form.name.value,
        email: form.email.value,
        message: form.message.value,
        locale: i18n.language?.slice(0, 2) || "ro",
      })
      toast.success(t("pages.contact.success"))
      form.reset()
    } catch (err) {
      toast.error(err.response?.data?.detail || t("pages.contact.error"))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <MarketingLayout seoKey="contact" jsonLd={jsonLd}>
      <div className="pt-36 pb-24">
        <div className="mx-auto max-w-3xl px-6 lg:px-10">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-5">
            {t("pages.contact.eyebrow")}
          </p>
          <h1 className="font-display text-4xl md:text-5xl tracking-tighter font-medium">{t("pages.contact.title")}</h1>
          <p className="mt-6 text-lg text-muted-foreground">{t("pages.contact.subtitle")}</p>
          <p className="mt-4 text-sm">
            <a href={`mailto:${email}`} className="text-accent hover:underline">
              {email}
            </a>
          </p>
        </div>

        <form onSubmit={onSubmit} className="mx-auto max-w-xl px-6 lg:px-10 mt-12 space-y-4">
          <div>
            <Label htmlFor="name">{t("auth.name")}</Label>
            <Input id="name" name="name" required className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="email">{t("auth.email")}</Label>
            <Input id="email" name="email" type="email" required className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="message">{t("pages.contact.message")}</Label>
            <Textarea id="message" name="message" required rows={5} className="mt-1.5" />
          </div>
          <Button type="submit" className="rounded-full w-full" disabled={submitting}>
            {submitting ? t("auth.pleaseWait") : t("pages.contact.submit")}
          </Button>
        </form>
      </div>
    </MarketingLayout>
  )
}
